import React from 'react';
import { Pressable, View } from 'react-native';
import { radius, useColors } from '../theme';
import { useT } from '../i18n';
import { Icon } from '../icons/Icon';
import { CountBadge } from './CountBadge';
import { T } from './Text';

/**
 * Sticky bar above the order flow: back on the left, where you are in the
 * middle, and the running item count plus order button on the right.
 */
export function StepHeader({
  title,
  subtitle,
  onBack,
  itemCount,
  onViewCart,
}: {
  title: string;
  subtitle?: string;
  onBack?: () => void;
  itemCount: number;
  onViewCart?: () => void;
}) {
  const colors = useColors();
  const t = useT();

  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        paddingVertical: 12,
        borderBottomWidth: 1,
        borderBottomColor: colors.line,
        marginBottom: 16,
      }}
    >
      {onBack ? (
        <Pressable
          onPress={onBack}
          style={({ hovered }: any) => ({
            flexDirection: 'row',
            alignItems: 'center',
            gap: 6,
            paddingVertical: 8,
            paddingHorizontal: 11,
            borderRadius: radius.md,
            borderWidth: 1,
            borderColor: hovered ? colors.gold : colors.line,
            backgroundColor: colors.surface,
          })}
        >
          <Icon name="back" size={16} color={colors.textDim} />
          <T size={12.5} weight={700} color={colors.textDim}>
            {t('back')}
          </T>
        </Pressable>
      ) : null}

      <View style={{ flex: 1, gap: 2, minWidth: 0 }}>
        <T size={15} weight={700} serif numberOfLines={1}>
          {title}
        </T>
        {subtitle ? (
          <T size={11.5} color={colors.textFaint} numberOfLines={1}>
            {subtitle}
          </T>
        ) : null}
      </View>

      {onViewCart ? (
        <Pressable
          onPress={onViewCart}
          style={({ pressed }) => ({
            flexDirection: 'row',
            alignItems: 'center',
            gap: 8,
            paddingVertical: 9,
            paddingHorizontal: 13,
            borderRadius: radius.md,
            backgroundColor: itemCount > 0 ? colors.gold : colors.surface,
            borderWidth: 1,
            borderColor: itemCount > 0 ? colors.gold : colors.line,
            opacity: pressed ? 0.85 : 1,
          })}
        >
          <Icon
            name="order"
            size={16}
            color={itemCount > 0 ? colors.onGold : colors.textDim}
          />
          <T
            size={12.5}
            weight={700}
            color={itemCount > 0 ? colors.onGold : colors.textDim}
          >
            {t('view_cart')}
          </T>
          {itemCount > 0 ? (
            <View
              style={{
                minWidth: 20,
                paddingHorizontal: 6,
                paddingVertical: 1,
                borderRadius: radius.pill,
                backgroundColor: colors.onGold,
              }}
            >
              <T size={11} weight={800} color={colors.gold}>
                {itemCount}
              </T>
            </View>
          ) : null}
        </Pressable>
      ) : itemCount > 0 ? (
        <CountBadge value={itemCount} />
      ) : null}
    </View>
  );
}
